import { Component, input, output, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpEventType } from '@angular/common/http';
import { Apollo, gql } from 'apollo-angular';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoPipe } from '@jsverse/transloco';

const GENERATE_UPLOAD_URL = gql`
  mutation GenerateUploadUrl($filename: String!, $contentType: String!) {
    generateUploadUrl(filename: $filename, contentType: $contentType) {
      uploadUrl
      path
      bucket
    }
  }
`;

@Component({
  selector: 'dhats-file-uploader',
  standalone: true,
  imports: [CommonModule, MatProgressBarModule, MatIconModule, TranslocoPipe],
  templateUrl: './file-uploader.component.html',
  styleUrl: './file-uploader.component.scss',
})
export class FileUploaderComponent {
  private readonly http = inject(HttpClient);
  private readonly apollo = inject(Apollo);

  label = input<string>('Upload File');
  accept = input<string>('*/*');
  isClient = input<boolean>(false);
  maxSizeMb = input<number>(1024); // 1GB default limit

  fileUploaded = output<{ bucket: string; path: string; filename: string }>();

  isDragOver = signal<boolean>(false);
  uploading = signal<boolean>(false);
  progress = signal<number>(0);
  currentFile = signal<File | null>(null);
  errorMessage = signal<string | null>(null);

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(true);
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(false);
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(false);
    this.errorMessage.set(null);

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFileSelection(files[0]!);
    }
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    this.errorMessage.set(null);

    if (files && files.length > 0) {
      this.handleFileSelection(files[0]!);
    }
  }

  private handleFileSelection(file: File) {
    const maxBytes = this.maxSizeMb() * 1024 * 1024;
    if (file.size > maxBytes) {
      this.errorMessage.set(`File size exceeds limit of ${this.maxSizeMb()}MB`);
      return;
    }

    this.currentFile.set(file);
    this.uploadFile(file);
  }

  private getMimeType(filename: string, defaultType: string): string {
    const ext = filename.split('.').pop()?.toLowerCase();
    const mimeMap: Record<string, string> = {
      'json': 'application/json',
      'pdf': 'application/pdf',
      'png': 'image/png',
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'gif': 'image/gif',
      'webp': 'image/webp',
      'svg': 'image/svg+xml',
      'txt': 'text/plain',
      'zip': 'application/zip',
    };
    return (ext && mimeMap[ext]) || defaultType || 'application/octet-stream';
  }

  private uploadFile(file: File) {
    this.uploading.set(true);
    this.progress.set(0);

    const clientName = this.isClient() ? 'client' : undefined;
    const resolvedType = this.getMimeType(file.name, file.type);

    this.apollo
      .mutate<any>({
        mutation: GENERATE_UPLOAD_URL,
        variables: {
          filename: file.name,
          contentType: resolvedType,
        },
        context: {
          clientName,
        },
      })
      .subscribe({
        next: ({ data }) => {
          if (!data || !data.generateUploadUrl) {
            this.errorMessage.set('Failed to generate upload signed URL');
            this.uploading.set(false);
            return;
          }

          const { uploadUrl, path, bucket } = data.generateUploadUrl;

          // PUT the file to GCS signed URL
          this.http
            .put(uploadUrl, file, {
              headers: {
                'Content-Type': resolvedType,
              },
              reportProgress: true,
              observe: 'events',
            })
            .subscribe({
              next: (event) => {
                if (event.type === HttpEventType.UploadProgress) {
                  this.progress.set(Math.round((100 * event.loaded) / (event.total ?? 1)));
                } else if (event.type === HttpEventType.Response) {
                  this.uploading.set(false);
                  this.fileUploaded.emit({
                    bucket,
                    path,
                    filename: file.name,
                  });
                }
              },
              error: (err) => {
                console.error('Error in GCS direct PUT upload:', err);
                this.errorMessage.set('Failed to upload file to storage');
                this.uploading.set(false);
              },
            });
        },
        error: (err) => {
          console.error('Error generating signed URL:', err);
          const gqlMessage = err?.graphQLErrors?.[0]?.message || err?.message;
          this.errorMessage.set(gqlMessage || 'Server authorization failed for upload');
          this.uploading.set(false);
        },
      });
  }

  clearFile() {
    this.currentFile.set(null);
    this.progress.set(0);
    this.uploading.set(false);
    this.errorMessage.set(null);
  }
}
